import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { updateCourse } from "../../../redux/Actions/courseCreateActions";

const courseTypes = ["Normal", "Intensive"];
const weekdays = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const EditCourseModal = ({ open, onClose, course }) => {
  const dispatch = useDispatch();

  const [courseType, setCourseType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dayTimes, setDayTimes] = useState([
    { dayOfWeek: "", startTime: "", endTime: "" },
  ]);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (course) {
      setCourseType(course.courseType || "");
      setStartDate(course.startDate?.split("T")[0] || "");
      setEndDate(course.endDate?.split("T")[0] || "");
      if (course.dayTimes?.length) {
        setDayTimes(
          course.dayTimes.map((dt) => ({
            dayOfWeek: dt.dayOfWeek || "",
            startTime: dt.startTime || "",
            endTime: dt.endTime || "",
          }))
        );
      } else if (
        course.daysOfWeek?.length &&
        course.startTime &&
        course.endTime
      ) {
        setDayTimes(
          course.daysOfWeek.map((day) => ({
            dayOfWeek: day,
            startTime: course.startTime,
            endTime: course.endTime,
          }))
        );
      } else {
        setDayTimes([{ dayOfWeek: "", startTime: "", endTime: "" }]);
      }
    }
  }, [course]);

  const handleSave = () => {
    const timeToMinutes = (value) => {
      const [h, m] = value.split(":").map(Number);
      return h * 60 + m;
    };
    const daySet = new Set();
    for (const dt of dayTimes) {
      if (!dt.dayOfWeek) {
        setValidationError("Please select a day for each schedule entry.");
        return;
      }
      if (daySet.has(dt.dayOfWeek)) {
        setValidationError("Duplicate days are not allowed in the schedule.");
        return;
      }
      daySet.add(dt.dayOfWeek);
      if (!dt.startTime || !dt.endTime) {
        setValidationError(
          `Please set start and end time for ${dt.dayOfWeek}.`
        );
        return;
      }
      if (timeToMinutes(dt.endTime) <= timeToMinutes(dt.startTime)) {
        setValidationError(
          `End time must be after start time for ${dt.dayOfWeek}.`
        );
        return;
      }
    }

    setValidationError("");
    const normalizedDayTimes = dayTimes.map((dt) => ({
      dayOfWeek: dt.dayOfWeek,
      startTime: dt.startTime,
      endTime: dt.endTime,
    }));
    const daysPerWeek = normalizedDayTimes.length;
    dispatch(
      updateCourse(course.teacherCourseId, {
        courseType,
        daysPerWeek,
        startDate,
        endDate,
        dayTimes: normalizedDayTimes,
      })
    ).then(() => {
      onClose();
    });
  };

  const handleDayTimeChange = (index, field, value) => {
    setDayTimes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddDayTime = () => {
    setDayTimes((prev) => [
      ...prev,
      { dayOfWeek: "", startTime: "", endTime: "" },
    ]);
  };

  const handleRemoveDayTime = (index) => {
    setDayTimes((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Course</DialogTitle>
      <DialogContent>
        <TextField
          select
          label="Course Type"
          fullWidth
          margin="normal"
          value={courseType}
          onChange={(e) => setCourseType(e.target.value)}
        >
          {courseTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          type="date"
          label="Start Date"
          fullWidth
          margin="normal"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          type="date"
          label="End Date"
          fullWidth
          margin="normal"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Schedule (per day)
          </Typography>
          {dayTimes.map((dt, index) => (
            <Box key={`${dt.dayOfWeek}-${index}`} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <TextField
                  select
                  label="Day"
                  value={dt.dayOfWeek}
                  onChange={(e) =>
                    handleDayTimeChange(index, "dayOfWeek", e.target.value)
                  }
                  sx={{ minWidth: 160 }}
                >
                  {weekdays.map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  type="time"
                  label="Start Time"
                  value={dt.startTime}
                  onChange={(e) =>
                    handleDayTimeChange(index, "startTime", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  type="time"
                  label="End Time"
                  value={dt.endTime}
                  onChange={(e) =>
                    handleDayTimeChange(index, "endTime", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleRemoveDayTime(index)}
                  disabled={dayTimes.length === 1}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
          <Button variant="outlined" onClick={handleAddDayTime}>
            Add Day
          </Button>
        </Box>
        {validationError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {validationError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" sx={{ mr: 4 }}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCourseModal;
